import React, { useState, useEffect } from 'react';
import { ArrowRight, ArrowLeft, Check, Lock } from 'lucide-react';

const apiUrl = "https://rcgvpro-onboarding-f5864945e68e.herokuapp.com";
const DEFAULT_FRONTEND_URL = window.location.origin;

// The Stripe Connect redirect reloads the SPA and wipes React state, so we stash the
// form (including the new Stripe account id) here to survive the round-trip back to us.
const ONBOARDING_STORAGE_KEY = 'rcgvpro_onboarding';


const PAYMENT_OPTIONS = [
    {
        label: "No Referral",
        value: "1",
        percent: 0,
        description: "No referral fee applied (0%)"
    },
    {
        label: "Yes, 5%",
        value: "2",
        percent: 5,
        description: "A 5% referral fee will be applied"
    },
    {
        label: "Yes, 10%",
        value: "3",
        percent: 10,
        description: "A 10% referral fee will be applied"
    },
    {
        label: "Yes, 15%",
        value: "4",
        percent: 15,
        description: "A 15% referral fee will be applied"
    },
    {
        label: "Yes, 20%",
        value: "5",
        percent: 20,
        description: "A 20% referral fee will be applied"
    }
];

const referralPercentFor = (option) => {
    const match = PAYMENT_OPTIONS.find((o) => o.value === option);
    return match ? match.percent : 0;
};

// Internal bookkeeping fields — not part of the information the user reviews.
const HIDDEN_REVIEW_FIELDS = ['stripeAccountId', 'referralCode'];

const DevTools = ({ formData, setFormData }) => {
    if (process.env.NODE_ENV !== 'development') return null;

    const fillTestData = () => {
        setFormData(prev => ({
            ...prev,
            email: 'test@example.com',
            firstName: 'John',
            lastName: 'Doe',
            position: 'Manager',
            workNumber: '(555) 555-1234',
            cellNumber: '(555) 555-5678',
            firmName: 'Test Company',
            address1: '123 Test St',
            city: 'Test City',
            state: 'TX',
            postalCode: '12345',
            paymentOption: '3' // Default to 10% referral fee for testing
        }));
    };
    
    return (
        <div className="bg-yellow-50 p-4 mb-4 rounded border border-yellow-300">
            <h4 className="font-bold mb-2 text-yellow-800">Development Testing Tools</h4>
            <button
                onClick={fillTestData}
                className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600"
            >
                Fill Test Data
            </button>
        </div>
    );
};

const OnboardingForm = () => {
    const totalSteps = 5;
    const [currentStep, setCurrentStep] = useState(1);
    const [isStripeLoading, setIsStripeLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [codeCopied, setCodeCopied] = useState(false);
    const [formData, setFormData] = useState({
        email: process.env.NODE_ENV === 'development' ? 'test@example.com' : '',
        firstName: '',
        lastName: '',
        position: '',
        workNumber: '',
        cellNumber: '',
        firmName: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        postalCode: '',
        paymentOption: process.env.NODE_ENV === 'development' ? '3' : '', // Default 10% referral fee in development mode
        assistantName: '',
        assistantPhoneNumber: '',
        stripeAccountId: '',
        referralCode: '' // assigned by the server on submit; shown on the final screen
    });

    // On return from Stripe Connect onboarding, restore the saved form (the redirect
    // wiped in-memory state) and — on success — persist the Stripe account id to Caspio.
    useEffect(() => {
        const status = new URLSearchParams(window.location.search).get('onboarding');
        if (status !== 'complete' && status !== 'refresh') return;

        let saved = null;
        try {
            const raw = sessionStorage.getItem(ONBOARDING_STORAGE_KEY);
            if (raw) saved = JSON.parse(raw);
        } catch (_) { /* ignore corrupt/unavailable storage */ }

        if (saved) setFormData(saved);

        if (status === 'refresh') {
            // The account link expired before completion. Send them back to the Connect
            // step with their data intact; keep the saved copy for the next return.
            setCurrentStep(5);
            return;
        }

        // status === 'complete': re-send the form so the record is fully up to date. The
        // backend already stored the Stripe account id when it created the account, so
        // this is a safety net rather than the only chance to save it.
        if (saved && saved.stripeAccountId) {
            (async () => {
                try {
                    const resp = await fetch(`${apiUrl}/api/submit-onboarding`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                        body: JSON.stringify(saved)
                    });
                    if (resp.ok) {
                        // Recover the referral code here too, in case this session began
                        // before the code was being returned on submit.
                        try {
                            const data = await resp.json();
                            if (data && data.referralCode) {
                                setFormData(prev => ({ ...prev, referralCode: data.referralCode }));
                            }
                        } catch (_) { /* non-fatal */ }
                        sessionStorage.removeItem(ONBOARDING_STORAGE_KEY);
                    } else {
                        console.error('Failed to save Stripe account id to Caspio:', resp.status, await resp.text());
                    }
                } catch (err) {
                    console.error('Error saving Stripe account id to Caspio:', err);
                }
            })();
        }

        setCurrentStep(6);
    }, []);

    // Add these functions after your state declarations and before renderInputField

const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Phone number formatting
    if (name === 'workNumber' || name === 'cellNumber' || name === 'assistantPhoneNumber') {
        // Remove all non-digits
        const digits = value.replace(/\D/g, '');
        
        // Format as (XXX) XXX-XXXX
        if (digits.length <= 10) {
            let formattedValue = digits;
            if (digits.length >= 3) {
                formattedValue = `(${digits.slice(0, 3)})${digits.length > 3 ? ' ' : ''}${digits.slice(3)}`;
            }
            if (digits.length >= 6) {
                formattedValue = `${formattedValue.slice(0, 9)}-${formattedValue.slice(9)}`;
            }
            setFormData(prev => ({
                ...prev,
                [name]: formattedValue
            }));
        }
        return;
    }

    setFormData(prev => ({
        ...prev,
        [name]: value
    }));
};

const validateForm = () => {
    const requiredFields = {
        email: 'Email',
        firstName: 'First Name',
        lastName: 'Last Name',
        position: 'Position',
        workNumber: 'Work Phone',
        cellNumber: 'Cell Phone',
        firmName: 'Firm Name',
        address1: 'Address Line 1',
        city: 'City',
        state: 'State',
        postalCode: 'Postal Code',
        paymentOption: 'Referral Fee Option'
    };

    const missingFields = Object.entries(requiredFields)
        .filter(([key]) => !formData[key])
        .map(([_, label]) => label);

    if (missingFields.length > 0) {
        alert(`Please fill in the following required fields:\n${missingFields.join('\n')}`);
        return false;
    }

    // Phone number validation
    const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;
    if (!phoneRegex.test(formData.workNumber)) {
        alert('Please enter a valid work phone number in the format (XXX) XXX-XXXX');
        return false;
    }
    if (!phoneRegex.test(formData.cellNumber)) {
        alert('Please enter a valid cell phone number in the format (XXX) XXX-XXXX');
        return false;
    }

    return true;
};

const renderPaymentOptionField = () => (
    <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
            Payment Collection Preference <span className="text-red-500">*</span>
        </label>
        <div className="space-y-4">
            {PAYMENT_OPTIONS.map((option) => (
                <div key={option.value} className="flex items-start">
                    <div className="flex items-center h-5">
                        <input
                            type="radio"
                            name="paymentOption"
                            value={option.value}
                            checked={formData.paymentOption === option.value}
                            onChange={handleInputChange}
                            className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                            required
                        />
                    </div>
                    <div className="ml-3">
                        <label className="font-medium text-gray-700">{option.label}</label>
                        <p className="text-gray-500 text-sm">{option.description}</p>
                    </div>
                </div>
            ))}
        </div>
    </div>
);
    const renderInputField = (label, name, placeholder, required = true, type = "text") => (
        <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
                {label} {required && <span className="text-red-500">*</span>}
            </label>
            <input
                type={type}
                name={name}
                value={formData[name]}
                onChange={handleInputChange}
                placeholder={placeholder}
                required={required}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
        </div>
    );

    const renderAutoFilledField = (label, value, onChange) => (
        <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
                {label}
            </label>
            <div className="relative">
                <input
                    type="email"
                    name="email"
                    value={value}
                    onChange={onChange}
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-500"
                />
                <Lock className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
            </div>
            <p className="mt-1 text-xs text-gray-500">Auto-filled from your registration</p>
        </div>
    );

    const handleSubmitForm = async () => {
        setIsSubmitting(true);
        
        if (!validateForm()) {
            setIsSubmitting(false);  // Reset if validation fails
            return;
        }
        
    try {
        const response = await fetch(`${apiUrl}/api/submit-onboarding`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: JSON.stringify(formData)
        });

            if (!response.ok) {
                let message = `Submission failed: ${response.status}`;
                try {
                    const errorData = await response.json();
                    if (errorData && errorData.error) message = errorData.error;
                } catch (_) {}
                throw new Error(message);
            }

            // The server derives the referral code from the email — keep it so the
            // confirmation screen can show the Pro the code they need for DocuSign.
            try {
                const data = await response.json();
                if (data && data.referralCode) {
                    setFormData(prev => ({ ...prev, referralCode: data.referralCode }));
                }
            } catch (_) { /* code will still be recoverable from the portal */ }

            setCurrentStep(5);
        } catch (error) {
            console.error('Form submission error:', error);
            alert(`Error submitting form: ${error.message}. Please try again or contact support.`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleStripeConnect = async () => {
        const apiUrl = "https://rcgvpro-onboarding-f5864945e68e.herokuapp.com";

        console.log('Current apiUrl:', apiUrl);
        console.log('Current window.location:', window.location.toString());
        
        setIsStripeLoading(true);
        try {
            const returnUrl = `https://${window.location.host}/?onboarding=complete`;
            const refreshUrl = `https://${window.location.host}/?onboarding=refresh`;
            
            const fullUrl = `${apiUrl}/api/create-connect-account`;
            console.log('Making request to:', fullUrl);
            
            const response = await fetch(fullUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                mode: 'cors',  // ✅ Set back to 'cors' to allow response reading
                credentials: 'include',  // ✅ Ensures cookies & sessions are sent
                body: JSON.stringify({
                    email: formData.email,
                    name: `${formData.firstName} ${formData.lastName}`,
                    company: formData.firmName,
                    returnUrl,
                    refreshUrl
                })
            });          
            
            console.log('📡 Fetch Response:', response);

            
    
            if (!response.ok) {
                const errorText = await response.text();
                console.error('Error response:', errorText);
                throw new Error(`HTTP error! status: ${response.status}`);
            }
    
            const data = await response.json();
            console.log('Response data:', data);
    
            if (data.accountLink) {
                // Persist the form + new Stripe account id BEFORE navigating away. The
                // redirect reloads the SPA and clears React state, so sessionStorage is
                // the only way the account id survives the round-trip back to us.
                const toPersist = { ...formData, stripeAccountId: data.accountId || '' };
                if (data.accountId) {
                    setFormData(toPersist);
                }
                try {
                    sessionStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(toPersist));
                } catch (_) { /* storage unavailable — nothing more we can do */ }
                window.location.href = data.accountLink;
            } else {
                throw new Error('No account link received from server');
            }
        } catch (error) {
            console.error('Stripe connection error:', error);
            alert(`Error connecting to Stripe: ${error.message}. Please check the console for more details.`);
        } finally {
            setIsStripeLoading(false);
        }
    };
   

    const renderStepContent = (step) => {
        switch (step) {
            case 1:
                console.log('Rendering step 1 content');
                return (
                    <div className="max-w-2xl mx-auto p-6">
                        <div className="space-y-4">
                            <h3 className="text-xl font-semibold text-gray-900">
                                Personal Information
                            </h3>
                            <p className="text-gray-600 mb-6">
                                Please verify your contact information
                            </p>

                            {renderAutoFilledField('Email', formData.email, handleInputChange)}
                            {renderInputField('First Name', 'firstName', 'Your first name')}
                            {renderInputField('Last Name', 'lastName', 'Your last name')}
                            {renderInputField('Position', 'position', 'Your role')}
                            {renderInputField('Work Phone', 'workNumber', '(XXX) XXX-XXXX', true, 'tel')}
                            {renderInputField('Cell Phone', 'cellNumber', '(XXX) XXX-XXXX', true, 'tel')}
                        </div>
                    </div>
                );
            case 2:
                return (
                    <div className="space-y-4">
                        <h3 className="text-xl font-semibold text-gray-900">Business Details</h3>
                        <p className="text-gray-600 mb-6">Enter your business information</p>
    
                        {renderInputField('Firm Name', 'firmName', 'Your company name')}
                        {renderPaymentOptionField()} {/* Updated to use the correct dropdown */}
                        {renderInputField('Address Line 1', 'address1', 'Street address')}
                        {renderInputField('Address Line 2', 'address2', 'Suite, unit, etc.', false)}
    
                        <div className="grid grid-cols-2 gap-4">
                            {renderInputField('City', 'city', 'City')}
                            {renderInputField('State', 'state', 'State')}
                        </div>
                        {renderInputField('Postal Code', 'postalCode', 'ZIP code')}
                    </div>
                );
            case 3:
                return (
                    <div className="space-y-4">
                        <h3 className="text-xl font-semibold text-gray-900">Assistant Information</h3>
                        <p className="text-gray-600 mb-6">Optional: Add your assistant's contact details</p>
    
                        {renderInputField('Assistant Name', 'assistantName', 'Full name', false)}
                        {renderInputField('Assistant Phone', 'assistantPhoneNumber', '(XXX) XXX-XXXX', false, 'tel')}
                    </div>
                );
            case 4:
                return (
                    <div className="space-y-4">
                        <h3 className="text-xl font-semibold text-gray-900">Review & Complete</h3>
                        <p className="text-gray-600 mb-6">Please review your information before completing registration</p>
    
                        <div className="space-y-3">
                            {Object.entries(formData).map(([key, value]) => (
                                value && !HIDDEN_REVIEW_FIELDS.includes(key) && (
                                    <div key={key} className="flex justify-between py-2 border-b border-gray-200">
                                        <span className="text-gray-600">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                                        <span className="text-gray-900 font-medium">{value}</span>
                                    </div>
                                )
                            ))}
                        </div>
                    </div> 
                );
                case 5:
                    return (
                        <div className="space-y-4">
                            <h3 className="text-xl font-semibold text-gray-900">Connect Your Bank Account</h3>
                            <p className="text-gray-600 mb-6">Set up payments with Stripe Connect</p>
                            
                            <div className="bg-blue-50 p-4 rounded-lg mb-6">
                                <p className="text-sm text-blue-900">
                                    To receive payments, you'll need to connect your bank account through Stripe.
                                    Your banking information is secured with industry-leading encryption.
                                </p>
                            </div>
                            {/* Removed the duplicate button here */}
                        </div>
                    );
                case 6: {
                    const feePercent = referralPercentFor(formData.paymentOption);
                    return (
                        <div className="space-y-6 text-center">
                            <div className="flex justify-center">
                                <div className="rounded-full bg-green-100 p-3">
                                    <Check className="h-8 w-8 text-green-600" />
                                </div>
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900">You're all set!</h3>
                            <p className="text-gray-600">
                                Your registration and Stripe payout setup are complete. You can now log in
                                to the RCGV Pro Referral portal to get started.
                            </p>

                            {feePercent > 0 && formData.referralCode && (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 text-left">
                                    <h4 className="font-semibold text-blue-900 mb-1">Your referral code</h4>
                                    <p className="text-sm text-blue-900 mb-3">
                                        Enter this code in the <strong>Product SKU</strong> field on every
                                        DocuSign engagement letter you send. It's how your {feePercent}%
                                        commission gets tracked and paid out to the bank account you just
                                        connected.
                                    </p>
                                    <div className="flex items-center gap-3">
                                        <code className="flex-1 bg-white border border-blue-300 rounded px-3 py-2 font-mono text-lg tracking-wide text-gray-900">
                                            {formData.referralCode}
                                        </code>
                                        <button
                                            onClick={() => {
                                                navigator.clipboard?.writeText(formData.referralCode)
                                                    .then(() => {
                                                        setCodeCopied(true);
                                                        setTimeout(() => setCodeCopied(false), 2000);
                                                    })
                                                    .catch(() => { /* user can select it manually */ });
                                            }}
                                            className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 whitespace-nowrap"
                                        >
                                            {codeCopied ? 'Copied!' : 'Copy'}
                                        </button>
                                    </div>
                                    <p className="mt-3 text-xs text-blue-800">
                                        Write this down now — you can also find it any time in the RCGV Pro
                                        Referral portal.
                                    </p>
                                </div>
                            )}

                            <a
                                href="https://www.rcgvaluation.com/rcgv-pro-referral"
                                className="inline-block px-6 py-3 bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                                Go to RCGV Pro Referral Login
                            </a>
                        </div>
                    );
                }

            default:
                return null;
        }
    };
        
    const renderActionButtons = () => {
        // Confirmation screen has its own CTA and no wizard navigation
        if (currentStep === 6) return null;

        const isLastStep = currentStep === 5;
        const isReviewStep = currentStep === 4;

        return (
            <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
                <button
                    onClick={() => setCurrentStep(prev => Math.max(1, prev - 1))}
                    disabled={currentStep === 1}
                    className={`flex items-center px-4 py-2 rounded ${
                        currentStep === 1 
                            ? 'text-gray-400 cursor-not-allowed' 
                            : 'text-blue-600 hover:bg-blue-50'
                    }`}
                >
                    <ArrowLeft className="h-5 w-5 mr-2" />
                    Previous
                </button>
                
                {isReviewStep ? (
                    <button
                        disabled={isSubmitting}
                        onClick={handleSubmitForm}
                        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                    >
                        {isSubmitting ? 'Submitting...' : 'Complete Registration'}
                    </button>
                ) : isLastStep ? (
                    <button
                        disabled={isStripeLoading}
                        onClick={handleStripeConnect}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                    >
                        {isStripeLoading ? 'Connecting...' : 'Connect with Stripe'}
                    </button>
                ) : (
                    <button
                        onClick={() => setCurrentStep(prev => Math.min(5, prev + 1))}
                        className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                        Continue
                        <ArrowRight className="h-5 w-5 ml-2" />
                    </button>
                )}
            </div>
        );
    };
    
    return (
        <div className="max-w-2xl mx-auto p-6">
            {process.env.NODE_ENV === 'development' && (
                <DevTools formData={formData} setFormData={setFormData} />
            )}
            <div className="mb-8">
                <img 
                    src="/RCGVPro_logo.png"
                    alt="RCGVPro Logo" 
                    className="h-12 mb-8"
                    onError={(e) => {
                        console.log('Logo failed to load');
                        e.target.style.display = 'none';
                    }}
                />
            </div>
            <div className="mb-8">
                <div className="flex justify-between mb-2">
                    {/* Progress steps */}
                </div>
            </div>
            <div className="bg-white rounded-lg shadow-lg p-6">
                {renderStepContent(currentStep)}
                {renderActionButtons()}
            </div>
        </div>
    );
};
    export default OnboardingForm;
